import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, Button, TextField, Radio, FormControlLabel, Checkbox, Divider, Tooltip, Typography
} from '@mui/material';
import { FaBell } from 'react-icons/fa';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MessageIcon from '@mui/icons-material/Message';
import LoopIcon from '@mui/icons-material/Loop';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InputIcon from '@mui/icons-material/Input';
import { saveAs } from 'file-saver';
import ReactFlow, {
  addEdge, applyNodeChanges, applyEdgeChanges, useReactFlow,
  MiniMap, Controls, Background, useUpdateNodeInternals
} from 'reactflow';
import 'reactflow/dist/style.css';

import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CustomNode from './CustomNode';
import ReplayIcon from '@mui/icons-material/Replay';

const nodeTypes = {
  default: CustomNode
};

// Funções utilitárias (mantidas iguais)
const obterChaveUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('chave') || '';
};

const NODE_ICONS = {
  TEXT: <MessageIcon sx={{ color: '#2196f3' }} fontSize="small" />,
  FILE: <InsertDriveFileIcon sx={{ color: '#B22222' }} fontSize="small" />,
  GPT: <SmartToyIcon sx={{ color: '#0055CC' }} fontSize="small" />,
  ATENDIMENTO: <LoopIcon sx={{ color: '#8800CC' }} fontSize="small" />,
  CADASTRAR: <AssignmentIndIcon sx={{ color: '#14631d' }} fontSize="small" />,
  EXCLUIR: <DeleteForeverIcon sx={{ color: '#c01414' }} fontSize="small" />,
  VCARD: <ContactPhoneIcon sx={{ color: '#2b8c2b' }} fontSize="small" />,
  CAPTURA: <InputIcon sx={{ color: '#DE911D' }} fontSize="small" />
};
const NODE_LABELS = {
  TEXT: "Etapa",
  FILE: "File",
  GPT: "GPT",
  ATENDIMENTO: "Trocar atendimento",
  CADASTRAR: "Cadastrar (BD)",
  EXCLUIR: "Excluir (BD)",
  VCARD: "vCard",
  CAPTURA: "Capturar Campo"
};

const getNextGlobalId = (pages) => {
  const allIds = pages.flatMap(p => p.nodes.map(n => n.id));
  let max = 0;
  for (const id of allIds) {
    const num = parseInt(id, 10);
    if (!isNaN(num) && num > max) max = num;
  }
  let next = max + 1 || 1;
  while (allIds.includes(String(next))) next++;
  return String(next);
};

const listarCamposCapturados = (pages) => {
  const set = new Set();
  pages.forEach(page =>
    page.nodes.forEach(n =>
      n.data.type === "CAPTURA" && n.data.capturaCampo && set.add(n.data.capturaCampo)
    )
  );
  return Array.from(set);
};

const CAMPOS_SISTEMA = ["SAUDACAO", "DATA_HORA", "DATA", "HORA", "RANDOM"];
const GPT_PLACEHOLDER = `Exemplo de Prompt:
Você é um atendente educado.
Responda dúvidas sobre os nossos produtos.
Nunca peça dados sensíveis.`;

const limitarLinhas = (texto, maxLinhas = 5) => {
  if (!texto) return '';
  const linhas = texto.split('\n');
  if (linhas.length <= maxLinhas) return texto;
  return linhas.slice(0, maxLinhas).join('\n') + '\n...';
};

export default function FlowEditor() {
  useEffect(() => {
    document.title = "Flow Editor";
  
    // Favicon emoji ⚡
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <text y=".9em" font-size="90">⚡</text>
    </svg>`;
    const href = "data:image/svg+xml," + encodeURIComponent(svg);
  
    // Reutiliza ou cria o link do favicon (evita duplicar)
    let favicon = document.querySelector('link[rel="icon"][data-flow-favicon]');
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.setAttribute("rel", "icon");
      favicon.setAttribute("data-flow-favicon", "1");
      document.head.appendChild(favicon);
    }
    favicon.type = "image/svg+xml";
    favicon.href = href;
  }, []);
  
  const [verificandoChave, setVerificandoChave] = useState(true);
  const [podeUsarFlow, setPodeUsarFlow] = useState(false);

  useEffect(() => {
    (async () => {
      setVerificandoChave(true);
      const chave = new URLSearchParams(window.location.search).get('chave');
      const resp = await fetch(
        `/api/fluxos/verificar-licenca?chave=${encodeURIComponent(chave)}`
      );
      const { podeUsarFlow } = await resp.json();
      setPodeUsarFlow(podeUsarFlow);
      setVerificandoChave(false);
    })();
  }, []);

  // MULTI-PÁGINA
  const [pages, setPages] = useState([
    {
      id: 1,
      name: 'Página 1',
      nodes: [
        { id: '1', type: 'default', position: { x: 250, y: 5 }, data: { id: '1', label: '🚀 Início\nDigite 1 para suporte\nDigite 2 para vendas', type: 'TEXT' } },
        { id: '2', type: 'default', position: { x: 100, y: 200 }, data: { id: '2', label: '🛠️ Suporte\nQual seu problema?', type: 'TEXT' } },
        { id: '3', type: 'default', position: { x: 400, y: 200 }, data: { id: '3', label: '🛒 Vendas\nQual produto você quer?', type: 'TEXT' } }
      ],
      edges: [
        { id: 'e1-2', source: '1', target: '2', label: '1' },
        { id: 'e1-3', source: '1', target: '3', label: '2' }
      ],
      startNodeId: '1',
      flowName: 'Novo',
    }
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const current = pages.find(p => p.id === currentPage);
  const setCurrentPageData = (data) =>
    setPages(prev => prev.map(p => p.id === currentPage ? { ...p, ...data } : p));

  // Página: adicionar, renomear, deletar
  const addPage = useCallback(() => {
    const newId = (Math.max(...pages.map(p => p.id)) + 1) || 2;
    setPages(prev => [
      ...prev,
      {
        id: newId,
        name: `Página ${newId}`,
        nodes: [],
        edges: [],
        startNodeId: '',
        flowName: `Página ${newId}`,
      }
    ]);
    setCurrentPage(newId);
  }, [pages]);
  const renamePage = useCallback(id => {
    const nm = prompt('Novo nome da página:');
    if (nm && nm.trim()) setPages(prev => prev.map(p => p.id === id ? { ...p, name: nm } : p));
  }, []);
  const deletePage = useCallback(id => {
    if (pages.length === 1) { alert('Não é possível excluir a única página!'); return; }
    if (!window.confirm('Excluir esta página?')) return;
    const rem = pages.filter(p => p.id !== id);
    setPages(rem);
    setCurrentPage(rem[0].id);
  }, [pages]);

  // Estados UI
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [showEditorPanel, setShowEditorPanel] = useState(true);
  const [flowKey, setFlowKey] = useState(0);
  const inputFileRef = useRef(null);
  const updateNodeInternals = useUpdateNodeInternals();
  const [erroCampo, setErroCampo] = useState('');
  const reactFlow = useReactFlow();

  useEffect(() => setErroCampo(''), [selectedNodeId]);

  useEffect(() => {
    setTimeout(() => {
      if (reactFlow && typeof reactFlow.fitView === "function") {
        reactFlow.fitView({ duration: 350 });
      }
    }, 120);
  }, [currentPage, current?.nodes?.length]);

  // Handler de Node Data
  const handleNodeDataChange = (field, value) => {
    setCurrentPageData({
      nodes: current.nodes.map(n => {
        if (n.id !== selectedNodeId) return n;
        if (n.data.type === "FILE" && field === "caminho") {
          return { ...n, data: { ...n.data, caminho: value, label: value } };
        }
        return { ...n, data: { ...n.data, [field]: value } };
      })
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };
  const handleLembreteChange = (campo, valor) => {
    setCurrentPageData({
      nodes: current.nodes.map(n =>
        n.id === selectedNodeId
          ? {
              ...n,
              data: {
                ...n.data,
                lembrete: {
                  ...((n.data && n.data.lembrete) || {}),
                  [campo]: valor
                }
              }
            }
          : n
      )
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };
  const handleLembreteAtivoChange = checked => {
    setCurrentPageData({
      nodes: current.nodes.map(n =>
        n.id === selectedNodeId
          ? {
              ...n,
              data: {
                ...n.data,
                lembrete: checked
                  ? { msg: '', tempo: '' }
                  : undefined
              }
            }
          : n
      )
    });
    setTimeout(() => updateNodeInternals(selectedNodeId), 0);
  };

  // Estilos dos nodes
  function getBorderColor(n) {
    if (n.id === current.startNodeId) return 'green';
    if (n.data.type === 'GPT') return '#0055CC';
    if (n.data.type === 'FILE') return '#B22222';
    if (n.data.type === 'CAPTURA') return '#DE911D';
    if (n.data.type === 'ELO') return '#004080';
    if (n.data.type === 'ATENDIMENTO') return '#8800CC';
    if (n.data.type === 'CADASTRAR') return '#14631d';
    if (n.data.type === 'EXCLUIR') return '#c01414';
    if (n.data.type === 'STOP') return '#880000';
    if (n.data.type === 'VCARD') return '#2b8c2b';
    return '#555';
  }
  function getBorderWidth(n) {
    return (n.id === selectedNodeId) ? 4 : 2;
  }
  function hasReminder(n) {
    return !!n.data.lembrete &&
      n.data.type !== "STOP" &&
      n.data.type !== "ELO" &&
      n.data.type !== "CADASTRAR" &&
      n.data.type !== "EXCLUIR";
  }

  // NODES estilizados
  const styledNodes = current.nodes.map(n => {
    let contentLines = [];
    if (n.data.type === "VCARD") {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3 }}>
          {n.data.nome ? n.data.nome : <span style={{ color: '#aaa' }}>Preencha o nome...</span>}
        </div>
      );
      contentLines.push(
        <div style={{ fontSize: 14, color: '#444' }}>
          {n.data.telefone ? n.data.telefone : <span style={{ color: '#aaa' }}>Preencha o telefone...</span>}
        </div>
      );
    } else if (n.data.type === "FILE") {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3 }}>
          {n.data.caminho ? limitarLinhas(n.data.caminho, 5) : <span style={{ color: '#aaa' }}>Preencha o caminho do arquivo...</span>}
        </div>
      );
      if (n.data.conteudo) {
        contentLines.push(
          <div style={{ fontSize: 14, color: '#444', marginTop: 4, whiteSpace: "pre-line" }}>
            {limitarLinhas(n.data.conteudo, 5)}
          </div>
        );
      }
    } else if (n.data.type === "GPT") {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, whiteSpace: "pre-line" }}>
          {n.data.label && n.data.label.trim()
            ? limitarLinhas(n.data.label, 5)
            : <span style={{ color: '#aaa', fontStyle: 'italic' }}>{GPT_PLACEHOLDER}</span>}
        </div>
      );
    } else if (n.data.type === "EXCLUIR" || n.data.type === "CADASTRAR") {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, color: "#818" }}>
          {n.data.label && n.data.label.trim()
            ? limitarLinhas(n.data.label, 5)
            : n.data.type === "EXCLUIR"
              ? "Guardar contato em lista de exclusão"
              : "Guardar contato em lista de contatos"}
        </div>
      );
      if (n.data.label && n.data.label.trim()) {
        contentLines.push(
          <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>
            {limitarLinhas(n.data.label, 5)}
          </div>
        );
      }
    } else if (n.data.type === "CAPTURA") {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, whiteSpace: "pre-line" }}>
          {n.data.label && n.data.label.trim()
            ? limitarLinhas(n.data.label, 5)
            : <span style={{ color: '#aaa' }}>Preencha o conteúdo...</span>}
        </div>
      );
    } else {
      contentLines.push(
        <div style={{ fontWeight: 500, fontSize: 15, marginTop: 3, whiteSpace: "pre-line" }}>
          {n.data.label && n.data.label.trim()
            ? limitarLinhas(n.data.label, 5)
            : <span style={{ color: '#aaa' }}>Preencha o conteúdo...</span>}
        </div>
      );
    }
    const showIdBadge = !["ELO", "STOP", "RETURN"].includes(n.data.type);
    contentLines.unshift(
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
        fontSize: 16,
        fontWeight: 700,
        minHeight: 28,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {showIdBadge && (
            <span style={{
              fontSize: 12,
              color: '#888',
              background: '#fff',
              borderRadius: 4,
              padding: '1px 7px',
              border: '1px solid #ddd',
              fontWeight: 500,
              letterSpacing: 1,
              minWidth: 28,
              textAlign: 'center'
            }}>
              #{n.id}
            </span>
          )}
          {NODE_ICONS[n.data.type] || <MessageIcon sx={{ color: '#555' }} fontSize="small" />}
          <span style={{ marginLeft: 4 }}>{NODE_LABELS[n.data.type] || "Etapa"}</span>
        </div>
        {hasReminder(n) &&
          <Tooltip title="Esta etapa tem lembrete">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FaBell style={{ color: '#FFA500', fontSize: 17 }} />
            </span>
          </Tooltip>
        }
      </div>
    );
      if (['ELO', 'STOP', 'RETURN'].includes(n.data.type)) {
        // Cores e textos
        let cor = '#004080'; // ELO
        let texto = (n.data.destino && n.data.destino !== '' ? n.data.destino : '—');

        if (n.data.type === 'STOP') {
          cor = '#880000';
          texto = 'STOP';
        }
        if (n.data.type === 'RETURN') {
          cor = '#1976d2';
          texto = 'RETURN';
        }
        return {
          ...n,
          style: {
            border: `3px solid ${cor}`,
            background: "linear-gradient(120deg, #f2f7ff 30%, #fff 100%)",
            borderRadius: '50%',
            width: 60,
            minHeight: 60,
            height: 60,
            fontWeight: 'bold',
            fontSize: '0.50rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
            userSelect: 'none',
            color: cor,
            textTransform: 'uppercase'
          },
          data: {
            ...n.data,
            label: <div>{texto}</div>
          }
        };
      }
      if (n.data.type === 'CAPTURA') {
      const header = (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 16,
          fontWeight: 700
        }}>
          {NODE_ICONS.CAPTURA}
          <span style={{ marginLeft: 4 }}>{NODE_LABELS.CAPTURA}</span>
        </div>
      );
      const fieldName = (
        <div style={{
          fontSize: 14,
          color: '#555',
          marginTop: 6,
          textAlign: 'center'
        }}>
          {n.data.capturaCampo || <span style={{ color: '#ccc' }}>Sem campo definido</span>}
        </div>
      );
      return {
        ...n,
        style: {
          border: `${getBorderWidth(n)}px solid ${getBorderColor(n)}`,
          background: n.id === selectedNodeId
            ? 'linear-gradient(120deg, #fff4e5 30%, #fff9f0 90%)'
            : 'linear-gradient(120deg, #fff8e1 30%, #fffde7 100%)',
          borderRadius: '12px',
          width: 180,
          minHeight: 48,
          padding: '8px',
          boxSizing: 'border-box',
        },
        data: {
          ...n.data,
          label: <div>{[header, fieldName]}</div>
        }
      };
    }
    let bgGradient =
      n.id === selectedNodeId
        ? "linear-gradient(120deg, #e0ffe0 30%, #fff 90%)"
        : "linear-gradient(120deg, #f2f7ff 30%, #fff 100%)";
    return {
      ...n,
      style: {
        border: `${getBorderWidth(n)}px solid ${getBorderColor(n)}`,
        background: bgGradient,
        borderRadius: '12px',
        width: 220, minHeight: 64, maxWidth: 250,
        fontSize: '1rem',
        display: 'block',
        textAlign: 'left',
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        overflow: 'hidden',
        padding: '10px 14px',
        boxSizing: 'border-box',
        boxShadow: n.id === selectedNodeId
          ? "0 0 0 2px #66ff33, 0 3px 12px rgba(0,0,0,0.08)"
          : "0 1px 6px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.3s, background 0.3s, border 0.3s"
      },
      data: { ...n.data, label: <div>{contentLines}</div> }
    };
  });

  // Validação de edges igual ao anterior
  const onConnect = useCallback((params) => {
    const nodes = current.nodes;
    const edges = current.edges;
    const sourceNode = nodes.find(n => n.id === params.source);
    const targetNode = nodes.find(n => n.id === params.target);
    const tipo = (sourceNode?.data?.type || "").toUpperCase();

    if (tipo === "ELO") { alert("❌ ELO não pode ter saída."); return; }
    if (tipo === "STOP") { alert("❌ STOP não pode ter saída."); return; }
    if (tipo === "CAPTURA") {
      const saidas = edges.filter(e => e.source === sourceNode.id);
      if (saidas.length >= 1) { alert("❌ CAPTURA só pode ter uma saída."); return; }
      if (params.label && params.label.trim() !== "") { alert("❌ CAPTURA só pode ter transições automáticas (sem label)."); return; }
    }
    if (tipo === "GPT" && (targetNode?.data?.type || "").toUpperCase() === "ATENDIMENTO") {
      alert("❌ GPT não pode direcionar para um ATENDIMENTO."); return;
    }
    if (["FILE", "VCARD"].includes(tipo)) {
      const saidas = edges.filter(e => e.source === sourceNode.id);
      if (saidas.length >= 1) {
        alert(`❌ ${tipo} só pode ter uma saída.`); return;
      }
    }
    const promptMessage = [
      '- Use * para qualquer entrada (curinga)',
      '- Deixe em branco para avançar para a próxima etapa',
      '- Vários valores separe por vírgula (Ex: Não, Nao, N)'
    ].join('\n');
    const label = prompt(promptMessage, '')?.trim() || '';
    if (edges.some(e =>
      e.source === params.source &&
      e.target === params.target &&
      e.label === label
    )) {
      alert('Ligação duplicada!'); return;
    }
    setCurrentPageData({ edges: addEdge({ ...params, label }, edges) });
  }, [current]);

  // ReactFlow event handlers
  const onNodesChange = useCallback(changes => {
    setCurrentPageData({ nodes: applyNodeChanges(changes, current.nodes) });
  }, [current]);
  const onEdgesChange = useCallback(changes => {
    setCurrentPageData({ edges: applyEdgeChanges(changes, current.edges) });
  }, [current]);

  // Criação de nodes (ID global e sequencial!)
  const addNewNode = useCallback((type = 'TEXT') => {
    const id = getNextGlobalId(pages);
    let newNode;
    if (type === 'CAPTURA') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: '', type: 'CAPTURA', capturaCampo: '' }
      };
    } else if (type === 'ELO') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: ``, type: 'ELO', destino: "", retorno: "" }
      };
    } else if (type === 'RETURN') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, type: 'RETURN', label: '' }
      };
    } else if (type === 'STOP') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: 'STOP', type: 'STOP' }
      };
    } else if (type === 'FILE') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: '', type: 'FILE', caminho: '', conteudo: '' }
      };
    } else if (type === 'GPT') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: '', type: 'GPT' }
      };
    } else if (type === 'ATENDIMENTO') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: '📞 Novo Atendimento, vamos transferir o seu atendimento, aguarde...', type: 'ATENDIMENTO' }
      };
    } else if (type === 'CADASTRAR') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: '', type: 'CADASTRAR' }
      };
    } else if (type === 'EXCLUIR') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: '', type: 'EXCLUIR' }
      };
    } else if (type === 'VCARD') {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, type: 'VCARD', nome: '', telefone: '' }
      };
    } else {
      newNode = {
        id, type: 'default', position: { x: Math.random() * 600, y: Math.random() * 400 },
        data: { id, label: '', type }
      };
    }
    setCurrentPageData({ nodes: [...current.nodes, newNode] });
  }, [current, pages]);

  // Definir início e apagar nó
  const handleStartChange = useCallback(() => {
    if (selectedNodeId) {
      setCurrentPageData({ startNodeId: selectedNodeId });
    }
  }, [selectedNodeId]);
  const deleteSelectedNode = useCallback(() => {
    setCurrentPageData({
      nodes: current.nodes.filter(n => n.id !== selectedNodeId),
      edges: current.edges.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId),
      startNodeId: current.startNodeId === selectedNodeId ? '' : current.startNodeId
    });
    setSelectedNodeId(null);
  }, [current, selectedNodeId, setCurrentPageData]);

  // Import/Export MULTI-PÁGINA
  const onArquivoSelecionado = useCallback(e => {
    const file = e.target.files[0];
    if (!file) return;
    const nomeArquivo = file.name ? file.name.replace(/\.json$/i, '') : '';
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        let j = JSON.parse(ev.target.result);
        if (Array.isArray(j.pages)) {
          setPages(j.pages);
          setCurrentPage(j.pages[0]?.id || 1);
        } else if (j.nodes && j.edges) {
          setPages([{
            id: 1, name: nomeArquivo || 'Página 1',
            nodes: j.nodes, edges: j.edges, startNodeId: j.startNodeId || '',
            flowName: j.nome && String(j.nome).trim() !== '' ? j.nome : nomeArquivo
          }]);
          setCurrentPage(1);
        } else {
          throw new Error('Formato de arquivo inválido');
        }
        setSelectedNodeId(null);
      } catch {
        alert('Arquivo inválido');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);
  const exportarJson = () => {
    const blob = new Blob([JSON.stringify({ pages }, null, 2)], { type: 'application/json' });
    saveAs(blob, `fluxo-multipagina.json`);
  };

  // Campos variáveis (igual ao seu)
  function renderCamposVariaveis(nodes, labelField, handleChange, nodeType) {
    if (["ELO", "VCARD", "STOP"].includes(nodeType)) return null;
    const camposCaptura = listarCamposCapturados(pages);
    const camposSistema = CAMPOS_SISTEMA;
    if (camposCaptura.length === 0 && camposSistema.length === 0) return null;
    const renderBotaoCampo = (campo) => (
      <Button
        key={campo}
        size="small"
        sx={{
          minWidth: 0,
          mx: 0.5,
          my: 0.5,
          p: "2px 6px",
          fontSize: 13,
          color: '#1a237e',
          border: '1px solid #aaa',
          borderRadius: 1,
          background: "#fff"
        }}
        onClick={() => {
          let field = labelField || '';
          const insert = `{${campo}}`;
          if (document.activeElement && document.activeElement.tagName === "TEXTAREA") {
            const textarea = document.activeElement;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const before = field.slice(0, start);
            const after = field.slice(end);
            handleChange(before + insert + after);
            setTimeout(() => {
              textarea.focus();
              textarea.setSelectionRange(start + insert.length, start + insert.length);
            }, 10);
          } else {
            handleChange(field + insert);
          }
        }}
      >
        {`{${campo}}`}
      </Button>
    );
    return (
      <Box sx={{ mb: 2, mt: -1.5 }}>
        {camposCaptura.length > 0 && (
          <>
            <b style={{ fontSize: 13 }}>Campos capturados:</b><br />
            {camposCaptura.map(renderBotaoCampo)}
            <Divider sx={{ my: 1 }} />
          </>
        )}
        {camposSistema.length > 0 && (
          <>
            <b style={{ fontSize: 13 }}>Campos do sistema:</b><br />
            {camposSistema.map(renderBotaoCampo)}
          </>
        )}
      </Box>
    );
  }

  if (verificandoChave) {
    return (
      <Box sx={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }}>
        <h2>Verificando chave de acesso...</h2>
      </Box>
    );
  }
  if (!podeUsarFlow) {
    return (
      <Box sx={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }}>
        <h2 style={{ color: 'red' }}>Acesso não autorizado</h2>
        <p>
          Esta chave não permite acesso ao Flow Editor.<br />
          Verifique se a chave está correta ou se possui permissão.
        </p>
        <Button variant="contained" onClick={() => window.location.reload()}>Tentar novamente</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Abas de páginas */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: '1px solid #ccc', bgcolor: '#f1f1f1' }}>
        {pages.map(p => (
          <Box
            key={p.id}
            sx={{
              px: 2, py: 0.5, borderRadius: 1,
              bgcolor: p.id === currentPage ? '#fff' : '#e5e5e5',
              border: '1px solid #ccc',
              display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer'
            }}
            onClick={() => setCurrentPage(p.id)}
          >
            <span>{p.name}</span>
            <Button size="small" onClick={e => { e.stopPropagation(); renamePage(p.id); }}>✏️</Button>
            <Button size="small" onClick={e => { e.stopPropagation(); deletePage(p.id); }}>🗑️</Button>
          </Box>
        ))}
        <Button startIcon={<span>➕</span>} onClick={addPage}>Nova Página</Button>
      </Box>

      <Box sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'grey.100',
        flexWrap: 'wrap',
        boxShadow: '0 3px 12px 0 rgba(60,100,180,0.03)'
      }}>
        {/* BLOCO 1 — Importação e Exportação (Branco) */}
        <Tooltip title="Importar fluxo em JSON">
          <Button sx={{
            bgcolor: '#fff', color: '#172554', fontWeight: 700,
            border: '1.5px solid #e0e0e0'
          }} startIcon={<DownloadIcon />} onClick={() => inputFileRef.current.click()}>
            Importar JSON
          </Button>
        </Tooltip>
        <Tooltip title="Exportar fluxo em JSON">
          <Button sx={{
            bgcolor: '#fff', color: '#172554', fontWeight: 700,
            border: '1.5px solid #e0e0e0'
          }} startIcon={<ShareIcon />} onClick={exportarJson}>
            Exportar JSON
          </Button>
        </Tooltip>
      
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />
      
        {/* BLOCO 2 — Etapas principais (Vermelho forte/mais forte na Nova Etapa) */}
        <Tooltip title="Adicionar nova etapa de mensagem">
          <Button sx={{
            bgcolor: '#E53935', color: '#fff', fontWeight: 700,
            border: '1.5px solid #B71C1C'
          }} startIcon={<AddIcon />} onClick={() => addNewNode('TEXT')}>
            Nova Etapa
          </Button>
        </Tooltip>
        <Tooltip title="Criar ligação para outra etapa (ELO)">
          <Button sx={{
            bgcolor: '#EF9A9A', color: '#B71C1C', fontWeight: 700,
            border: '1.5px solid #E57373'
          }} startIcon={<AccountTreeIcon />} onClick={() => addNewNode('ELO')}>
            ELO
          </Button>
        </Tooltip>
        <Tooltip title="Etapa de retorno">
          <Button sx={{
            bgcolor: '#EF9A9A', color: '#B71C1C', fontWeight: 700,
            border: '1.5px solid #E57373'
          }} startIcon={<ReplayIcon />} onClick={() => addNewNode('RETURN')}>
            RETURN
          </Button>
        </Tooltip>
        <Tooltip title="Inserir etapa de parada">
          <Button sx={{
            bgcolor: '#EF9A9A', color: '#B71C1C', fontWeight: 700,
            border: '1.5px solid #E57373'
          }} startIcon={<StopCircleIcon />} onClick={() => addNewNode('STOP')}>
            STOP
          </Button>
        </Tooltip>
      
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />
      
        {/* BLOCO 3 — Arquivo e vCard (Laranja) */}
        <Tooltip title="Enviar arquivo no fluxo">
          <Button sx={{
            bgcolor: '#FF9800', color: '#fff', fontWeight: 700,
            border: '1.5px solid #F57C00'
          }} startIcon={<InsertDriveFileIcon />} onClick={() => addNewNode('FILE')}>
            FILE
          </Button>
        </Tooltip>
        <Tooltip title="Enviar contato no padrão vCard">
          <Button sx={{
            bgcolor: '#FF9800', color: '#fff', fontWeight: 700,
            border: '1.5px solid #F57C00'
          }} startIcon={<ContactPhoneIcon />} onClick={() => addNewNode('VCARD')}>
            vCARD
          </Button>
        </Tooltip>
      
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />
      
        {/* BLOCO 4 — Captura e Contato (Amarelo) */}
        <Tooltip title="Capturar informação digitada pelo usuário">
          <Button sx={{
            bgcolor: '#FFF9C4', color: '#000', fontWeight: 700,
            border: '1.5px solid #FBC02D'
          }} startIcon={<NoteAddIcon />} onClick={() => addNewNode('CAPTURA')}>
            Capturar
          </Button>
        </Tooltip>
        <Tooltip title="Cadastrar o usuário como contato">
          <Button sx={{
            bgcolor: '#FFF9C4', color: '#000', fontWeight: 700,
            border: '1.5px solid #FBC02D'
          }} startIcon={<AssignmentIndIcon />} onClick={() => addNewNode('CADASTRAR')}>
            Cadastrar Contato
          </Button>
        </Tooltip>
        <Tooltip title="Excluir o contato do usuário">
          <Button sx={{
            bgcolor: '#FFF9C4', color: '#000', fontWeight: 700,
            border: '1.5px solid #FBC02D'
          }} startIcon={<PersonRemoveIcon />} onClick={() => addNewNode('EXCLUIR')}>
            Excluir Contato
          </Button>
        </Tooltip>
      
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: '#e0e0e0' }} />
      
        {/* BLOCO 5 — Atendimento e GPT (Azul claro/mais claro) */}
        <Tooltip title="Transferir o atendimento para outro número">
          <Button sx={{
            bgcolor: '#90caf9', color: '#0d1330', fontWeight: 700,
            border: '1.5px solid #64b5f6'
          }} startIcon={<PhoneAndroidIcon />} onClick={() => addNewNode('ATENDIMENTO')}>
            Trocar Atendimento
          </Button>
        </Tooltip>
        <Tooltip title="Etapa de resposta com Inteligência Artificial">
          <Button sx={{
            bgcolor: '#90caf9', color: '#0d1330', fontWeight: 700,
            border: '1.5px solid #64b5f6'
          }} startIcon={<SmartToyIcon />} onClick={() => addNewNode('GPT')}>
            GPT
          </Button>
        </Tooltip>
      
        <Box sx={{ flexGrow: 1 }} />
      </Box>
                              
      {/* Canvas ReactFlow */}
      <Box sx={{
        flexGrow: 1, position: 'relative', background: '#f9f9f9'
      }}>
        <input ref={inputFileRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={onArquivoSelecionado} />
        <ReactFlow
          key={flowKey}
          nodes={styledNodes}
          edges={current.edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={(e, n) => { setSelectedNodeId(n.id); setSelectedEdgeId(null); setShowEditorPanel(true); }}
          nodeTypes={nodeTypes}


          onNodeDoubleClick={(e, n) => {
            if (n.data.type === "ELO") {
              const destinoId = n.data.destino;
              if (!destinoId) return;
          
              // Procurar a página de destino
              let paginaDoDestino = pages.find(p => p.nodes.some(nd => nd.id === destinoId));
              if (paginaDoDestino) {
                // Se não estiver na página correta, troca de página e seleciona o node
                if (paginaDoDestino.id !== currentPage) {
                  setCurrentPage(paginaDoDestino.id);
                  setTimeout(() => {
                    setSelectedNodeId(destinoId);
                    setSelectedEdgeId(null);
                    setShowEditorPanel(true);
                  }, 50); // timeout pra esperar a página renderizar os nodes
                } else {
                  setSelectedNodeId(destinoId);
                  setSelectedEdgeId(null);
                  setShowEditorPanel(true);
                }
              } else {
                // Se não achou o destino, só seleciona o ELO
                setSelectedNodeId(n.id);
                setSelectedEdgeId(null);
                setShowEditorPanel(true);
              }
            } else {
              setSelectedNodeId(n.id);
              setSelectedEdgeId(null);
              setShowEditorPanel(true);
            }
          }}

          onEdgeDoubleClick={(e, edge) => { e.stopPropagation(); setSelectedEdgeId(edge.id); setSelectedNodeId(null); setShowEditorPanel(true); }}
          onPaneClick={() => { setSelectedNodeId(null); setSelectedEdgeId(null); setShowEditorPanel(false); }}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
          style={{ height: '100%' }}
        >
          <Box
            sx={{
              '& .react-flow__controls-button': {
                width: 56,
                height: 56,
                fontSize: '2rem',
              },
              '& .react-flow__controls-button svg': {
                width: '2em',
                height: '2em',
              }
            }}
          >
            <Controls position="top-left" showInteractive />
          </Box>
          <MiniMap position="bottom-left" style={{ border: '1px solid #000', borderRadius: 4, background: 'rgba(255,255,255,0.85)' }} />
          <Background />
        </ReactFlow>

        {/* Sidebar Edição Edge */}
        {showEditorPanel && selectedEdgeId && (
          <Box component="aside" sx={{
            width: 320, p: 2, borderLeft: 1, borderColor: 'divider', bgcolor: 'grey.50',
            position: 'absolute', right: 0, top: 0, height: '100%', zIndex: 11
          }}>
            <TextField label="ID da ligação" value={selectedEdgeId} InputProps={{ readOnly: true }} fullWidth sx={{ mb: 2 }} />
            <TextField
              label="Label da ligação"
              value={current.edges.find(e => e.id === selectedEdgeId)?.label || ''}
              onChange={e => {
                const newLabel = e.target.value;
                setCurrentPageData({ edges: current.edges.map(edge => edge.id === selectedEdgeId ? { ...edge, label: newLabel } : edge) });
              }}
              fullWidth sx={{ mb: 2 }}
            />
            <Button
              variant="contained" color="error" fullWidth
              onClick={() => { setCurrentPageData({ edges: current.edges.filter(edge => edge.id !== selectedEdgeId) }); setSelectedEdgeId(null); }}
            >Apagar ligação</Button>
          </Box>
        )}

        {/* Sidebar Edição Nó */}
        {showEditorPanel && selectedNodeId && current.nodes.find(n => n.id === selectedNodeId) && (() => {
          const selectedNode = current.nodes.find(n => n.id === selectedNodeId);
          // CADASTRAR e EXCLUIR: mensagem opcional, lembrete sempre oculto!
          const isCadastrar = selectedNode?.data.type === 'CADASTRAR';
          const isExcluir = selectedNode?.data.type === 'EXCLUIR';
          return (
          <Box component="aside" sx={{ width: 360, p: 2, borderLeft: 1, borderColor: 'divider', bgcolor: 'grey.50', position: 'absolute', right: 0, top: 0, height: '100%', zIndex: 10 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                onClick={() => setShowEditorPanel(false)}
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<VisibilityOffIcon />}
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  letterSpacing: 1,
                  py: 1.3,
                  bgcolor: '#1976d2',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                Ocultar edição
              </Button>
            </Box>
            <TextField label="ID da Etapa" value={selectedNodeId} InputProps={{ readOnly: true }} fullWidth sx={{ mb: 2 }} />
            <TextField label="Tipo" value={selectedNode?.data.type || ''} InputProps={{ readOnly: true }} fullWidth sx={{ mb: 2 }} />
        
            {/* CAMPOS EDITÁVEIS — NÃO EXIBE PARA STOP E RETURN */}
            {['STOP', 'RETURN'].includes(selectedNode?.data.type)
              ? null
              : (
                <>
                  {(selectedNode?.data.type === 'CAPTURA') ? (
                    <>
                      <TextField
                        label="Campo para captura"
                        fullWidth
                        value={selectedNode?.data.capturaCampo || ""}
                        onChange={e => {
                          const val = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
                          if (val.length > 24) return;
                          const jaExiste = listarCamposCapturados(pages).filter(c => c !== selectedNode?.data.capturaCampo).includes(val);
                          if (jaExiste) setErroCampo('Já existe outro campo com esse nome');
                          else setErroCampo('');
                          handleNodeDataChange("capturaCampo", val);
                        }}
                        sx={{ mb: 1 }}
                        error={!!erroCampo}
                        helperText={erroCampo ? erroCampo : "Ex: NOME, CEP, TIME, NOME_CLIENTE"}
                        inputProps={{ maxLength: 24 }}
                      />
                      <Box sx={{ fontSize: 12, color: "#666", mb: 1, mt: -1 }}>
                        Apenas letras maiúsculas, números e _ (sem espaço)
                      </Box>
                    </>
                  ) : (selectedNode?.data.type === "VCARD") ? (
                    <>
                      <TextField
                        label="Nome do contato"
                        fullWidth
                        value={selectedNode?.data.nome || ''}
                        onChange={e => handleNodeDataChange('nome', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Digite o nome..."
                      />
                      <TextField
                        label="Telefone"
                        fullWidth
                        value={selectedNode?.data.telefone || ''}
                        onChange={e => handleNodeDataChange('telefone', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Digite o telefone..."
                      />
                    </>
                  ) : (selectedNode?.data.type === "FILE") ? (
                    <>
                      <TextField
                        label="Caminho do arquivo"
                        fullWidth
                        value={selectedNode?.data.caminho || ''}
                        onChange={e => handleNodeDataChange('caminho', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Ex: c:\caminho\file.ogg"
                      />
                      <TextField
                        label="Legenda (mensagem)"
                        fullWidth
                        multiline
                        rows={3}
                        value={selectedNode?.data.conteudo || ''}
                        onChange={e => handleNodeDataChange('conteudo', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Texto a ser enviada junto ao arquivo"
                      />
                      {renderCamposVariaveis(current.nodes, selectedNode?.data.conteudo, v => handleNodeDataChange('conteudo', v), "FILE")}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedNode?.data.lembrete}
                            onChange={e => handleLembreteAtivoChange(e.target.checked)}
                          />
                        }
                        label="Enviar lembrete se o usuário não responder"
                        sx={{ mb: 1, ml: 0 }}
                      />
                      {!!selectedNode?.data.lembrete && (
                        <>
                          <TextField
                            label="Mensagem do lembrete"
                            multiline
                            rows={2}
                            fullWidth
                            value={selectedNode?.data.lembrete?.msg || ''}
                            onChange={e => handleLembreteChange('msg', e.target.value)}
                            sx={{ mb: 1 }}
                            placeholder="Digite a mensagem a ser enviada..."
                          />
                          <TextField
                            label="Quando enviar (minutos)"
                            type="number"
                            fullWidth
                            value={selectedNode?.data.lembrete?.tempo || ''}
                            onChange={e => handleLembreteChange('tempo', e.target.value.replace(/\D/g, ''))}
                            sx={{ mb: 2 }}
                            placeholder="Ex: 60 (para 1h), 5, 120..."
                            inputProps={{ min: 1, maxLength: 6 }}
                          />
                        </>
                      )}
                    </>
                  ) : (selectedNode?.data.type === "ELO") ? (
                    <>
                      <TextField
                        label="Destino (ID da etapa)"
                        fullWidth
                        value={selectedNode?.data.destino || ""}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          handleNodeDataChange("destino", val);
                        }}
                        sx={{ mb: 2 }}
                        helperText="Digite o ID da etapa de destino (apenas número)."
                        inputProps={{ maxLength: 8 }}
                        placeholder="Ex: 4"
                      />
                      <TextField
                        label="Destino de retorno (ID ao voltar)"
                        fullWidth
                        value={selectedNode?.data.retorno || ""}
                        onChange={e => {
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          handleNodeDataChange("retorno", val);
                        }}
                        sx={{ mb: 2 }}
                        helperText="Para RETURN: pra onde volta quando terminar esse fluxo."
                        inputProps={{ maxLength: 8 }}
                        placeholder="Ex: 1"
                      />
                    </>
                  ) : (selectedNode?.data.type === "ATENDIMENTO") ? (
                    <>
                      <TextField
                        label="Mensagem para transferência"
                        multiline
                        rows={4}
                        fullWidth
                        value={selectedNode?.data.label || ''}
                        onChange={e => handleNodeDataChange('label', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder="Mensagem que será enviada ao transferir o atendimento"
                      />
                      {renderCamposVariaveis(current.nodes, selectedNode?.data.label, v => handleNodeDataChange('label', v), selectedNode?.data.type)}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedNode?.data.lembrete}
                            onChange={e => handleLembreteAtivoChange(e.target.checked)}
                          />
                        }
                        label="Enviar lembrete se o usuário não responder"
                        sx={{ mb: 1, ml: 0 }}
                      />
                      {!!selectedNode?.data.lembrete && (
                        <>
                          <TextField
                            label="Mensagem do lembrete"
                            multiline
                            rows={2}
                            fullWidth
                            value={selectedNode?.data.lembrete?.msg || ''}
                            onChange={e => handleLembreteChange('msg', e.target.value)}
                            sx={{ mb: 1 }}
                            placeholder="Digite a mensagem a ser enviada..."
                          />
                          <TextField
                            label="Quando enviar (minutos)"
                            type="number"
                            fullWidth
                            value={selectedNode?.data.lembrete?.tempo || ''}
                            onChange={e => handleLembreteChange('tempo', e.target.value.replace(/\D/g, ''))}
                            sx={{ mb: 2 }}
                            placeholder="Ex: 60 (para 1h), 5, 120..."
                            inputProps={{ min: 1, maxLength: 6 }}
                          />
                        </>
                      )}
                    </>
                  ) : (isCadastrar || isExcluir) ? (
                    <>
                      <TextField
                        label={isCadastrar ? "Mensagem para cadastrar contato" : "Mensagem para excluir contato"}
                        multiline
                        rows={4}
                        fullWidth
                        value={selectedNode?.data.label || ''}
                        onChange={e => handleNodeDataChange('label', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder={
                          isCadastrar
                            ? "Opcional: mensagem que será enviada ao cadastrar contato"
                            : "Opcional: mensagem que será enviada ao excluir contato"
                        }
                      />
                      {renderCamposVariaveis(current.nodes, selectedNode?.data.label, v => handleNodeDataChange('label', v), selectedNode?.data.type)}
                      <Typography variant="body2" sx={{ color: '#B22222', mt: -1, mb: 2 }}>
                        Se não preencher mensagem, só executa a ação e não envia texto.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <TextField
                        label="Conteúdo"
                        multiline
                        rows={5}
                        fullWidth
                        value={selectedNode?.data.label || ''}
                        onChange={e => handleNodeDataChange('label', e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder={
                          selectedNode?.data.type === "GPT"
                            ? GPT_PLACEHOLDER
                            : selectedNode?.data.type === "CADASTRAR"
                              ? "Mensagem para cadastrar contato"
                              : selectedNode?.data.type === "EXCLUIR"
                                ? "Mensagem para excluir contato"
                                : ""
                        }
                        InputProps={{
                          readOnly: selectedNode?.data.type === 'STOP'
                        }}
                      />
                      {renderCamposVariaveis(current.nodes, selectedNode?.data.label, v => handleNodeDataChange('label', v), selectedNode?.data.type)}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedNode?.data.lembrete}
                            onChange={e => handleLembreteAtivoChange(e.target.checked)}
                          />
                        }
                        label="Enviar lembrete se o usuário não responder"
                        sx={{ mb: 1, ml: 0 }}
                      />
                      {!!selectedNode?.data.lembrete && (
                        <>
                          <TextField
                            label="Mensagem do lembrete"
                            multiline
                            rows={2}
                            fullWidth
                            value={selectedNode?.data.lembrete?.msg || ''}
                            onChange={e => handleLembreteChange('msg', e.target.value)}
                            sx={{ mb: 1 }}
                            placeholder="Digite a mensagem a ser enviada..."
                          />
                          <TextField
                            label="Quando enviar (minutos)"
                            type="number"
                            fullWidth
                            value={selectedNode?.data.lembrete?.tempo || ''}
                            onChange={e => handleLembreteChange('tempo', e.target.value.replace(/\D/g, ''))}
                            sx={{ mb: 2 }}
                            placeholder="Ex: 60 (para 1h), 5, 120..."
                            inputProps={{ min: 1, maxLength: 6 }}
                          />
                        </>
                      )}
                    </>
                  )}
            
                  <FormControlLabel
                    control={
                      <Radio
                        checked={current.startNodeId === selectedNodeId}
                        onChange={handleStartChange}
                      />
                    }
                    label="Definir como início"
                    sx={{ mb: 2 }}
                  />
                </>
              )}
          
              <Button
                variant="contained"
                color="error"
                onClick={deleteSelectedNode}
                fullWidth
                startIcon={<DeleteForeverIcon />}
                sx={{ fontWeight: 700, letterSpacing: 1, py: 1.3 }}
              >
                Apagar etapa
              </Button>
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
}
